import React, {useState} from 'react';
import {Button, Box} from '@mui/material';
import {useSnackbar} from 'notistack';
import {ScrollableModal} from '../../components/Common/ScrollableModal';
import {ReportPackFormFragment} from '../../schema-types/ReportPackFormFragment';
import {useReportPack} from './useReportPack';
import {getReportPackDisabled, ReportPackContactProps} from './ReportPackHelpers';
import {ReportPackAddFormList} from './ReportPackAddFormList';
import {ReportPackExistingFormsList} from './ReportPackExistingFormsList';
import {ReportPackFormEditor} from './ReportPackFormEditor';
import {ReportPackStatus, ReportPackTemplateTypes} from '../../schema-types/globalTypes';
import {ColoredChip} from '../../components/EditorGrid/GridRenderComponents';
import {themeColors} from '../../theme/Theme';
import validateUUID from '../../utils/validateUUID';

export const ReportPackContact: React.FC<ReportPackContactProps> = ({
  contact,
  report,
}) => {
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState<ReportPackFormFragment | null>(null);
  const onOpenForm = (data: ReportPackFormFragment) => {
    setForm(data);
    setAddOpen(false);
  };
  const {enqueueSnackbar, closeSnackbar} = useSnackbar();
  const {onDeleteForm, onResendReportPackEmail} = useReportPack();

  const deleteForm = (formToDelete: ReportPackFormFragment) => {
    closeSnackbar();
    enqueueSnackbar(`Deleting ${formToDelete.formTypeLabel} form...`, {variant: 'info'});
    onDeleteForm({
      variables: {
        formId: formToDelete.id,
      },
    })
      .then(({data}) => {
        if (data?.deleteReportPackForm?.ok) {
          closeSnackbar();
          enqueueSnackbar(`Successfully deleted ${formToDelete.formTypeLabel} form`, {
            variant: 'success',
          });
        } else {
          throw Error(data?.deleteReportPackForm?.errors?.join(', '));
        }
      })
      .catch((_err) => {
        closeSnackbar();
        enqueueSnackbar(`Unable to delete ${formToDelete.formTypeLabel} form`, {
          variant: 'error',
        });
      });
  };
  const isDisabled = getReportPackDisabled(report) || !contact.email;
  const canResendEmail =
    report.status === ReportPackStatus.PARTLY_SIGNED ||
    report.status === ReportPackStatus.SUBMITTED ||
    report.status === ReportPackStatus.PARTLY_READ;

  const isReportSigned =
    (report.contactCuroIds.length > 0 &&
      contact.curoId === report.contactCuroIds[0] &&
      report.isSignedInClientPortal) ||
    (report.contactCuroIds.length > 1 &&
      contact.curoId === report.contactCuroIds[1] &&
      report.isLinkedSignedInClientPortal);

  const signedOrRead =
    report.templateType === ReportPackTemplateTypes.ATTACHMENT_LETTER ? 'signed' : 'read';
  return (
    <>
      <Box mb={1}>
        <ColoredChip
          colorScheme={{
            'Report not signed': themeColors.lightYellow,
            'Report not read': themeColors.lightYellow,
            'Report signed': themeColors.lightGreen,
            'Report read': themeColors.lightGreen,
          }}
          value={`Report${isReportSigned ? ' ' : ' not '}${signedOrRead}`}
        />
      </Box>
      <ScrollableModal size="large" open={!!form} onClose={() => setForm(null)}>
        <Box p={1} bgcolor="white">
          <ReportPackFormEditor
            form={form!}
            disabled={getReportPackDisabled(report)}
            onClose={() => setForm(null)}
          />
        </Box>
      </ScrollableModal>
      <ScrollableModal open={addOpen} onClose={() => setAddOpen(false)}>
        <ReportPackAddFormList
          templateType={report.templateType}
          contactCuroId={String(contact.curoId)}
          handleSuccess={onOpenForm}
          reportPackId={report.id}
        />
      </ScrollableModal>
      <ReportPackExistingFormsList
        disabled={isDisabled}
        onDelete={deleteForm}
        onEdit={onOpenForm}
        contact={contact}
        report={report}
      />
      <Box flex={1} mt={2} display="flex" justifyContent="center">
        {!isDisabled && (
          <Button
            onClick={() => {
              setAddOpen(true);
            }}
            variant="contained"
            color="primary"
          >
            Add form
          </Button>
        )}
        {canResendEmail ? (
          <Box ml={2}>
            <Button
              onClick={async () => {
                onResendReportPackEmail({
                  variables: {
                    contactCuroId: validateUUID(contact.curoId)!,
                    reportPackId: report.id,
                  },
                }).then(({data}) => {
                  if (data?.resendReportPackEmail?.ok) {
                    closeSnackbar();
                    enqueueSnackbar(`Resent email to ${contact.simplifiedName}`, {
                      variant: 'success',
                    });
                  } else {
                    closeSnackbar();
                    enqueueSnackbar(data?.resendReportPackEmail?.errors?.join(', '), {
                      variant: 'error',
                    });
                  }
                });
              }}
              variant="outlined"
              color="primary"
              data-testid="resend-email"
            >
              Resend notification to {`${contact.simplifiedName}`}
            </Button>
          </Box>
        ) : null}
      </Box>
    </>
  );
};
